import { isEmpty } from "lodash";
import { useState } from "react";

import { ActionsMenu, Button, RenderWhen } from "@components";
import {
  ActionsModalNames,
  BULK_ACTION_LABELS
} from "@components/ActionsModal";
import { useSelectRowsContext } from "@js2/hooks";
import { consts, settings } from "@js2/shim/jsbridge";
import tcbase from "@js2/shim/tcbase";
import { SUBSCRIPTION_ACTIONS } from "@modules/alertsconfiguration/const";
import { EnrichedFile } from "@modules/network/@types/common";
import AdvancedFetchModal from "@modules/samples/components/advanced_fetch/AdvancedFetchModal";
import useFetchAnalyze from "@modules/samples/hooks/useFetchAnalyze";

import {
  GRID_SCHEMA_MESSAGES,
  GRID_SELECTION_TAG_LIMIT,
  LOCATION
} from "../../../../../search/consts";
import BulkActionConfirmModal from "./BulkActionConfirmModal";

const { FEATURE_ALERTFEED } = settings || {};

const { userName, isSuperuser } = tcbase ?? {};

const {
  bulk_download_max_files: bulkDownloadMaxFiles,
  ticloud_max_fetch_file_size: ticloudMaxFetchFileSize,
  max_number_subscription_hashes: maxNumberSubscriptionHashes
} = consts ?? {};

type Props = {
  id: string;
  onToggle: (data: unknown, name: string) => void;
};

interface ConfirmationState {
  label: string;
  applicable: EnrichedFile[];
  nonApplicable: EnrichedFile[];
  onConfirm: () => void;
}

const canUserDelete = (samples: Array<EnrichedFile>) => {
  if (isSuperuser) return true;
  return samples.every(({ submission_users: users }) =>
    users.includes(userName ?? "")
  );
};

const getIsEveryExtracted = (samples: Array<EnrichedFile>) =>
  samples.every(({ submission_count: submissions }) => !submissions);

const getHasExtractedFiles = (samples: Array<EnrichedFile>) =>
  samples.some(({ file_count: count }) => count > 1);

const getExtractedFileCount = (samples: Array<EnrichedFile>) =>
  samples.reduce((acc, curr) => acc + (curr.file_count ?? 1) - 1, 0);

const getExtractedFilesMessage = (
  hasExtracted: boolean,
  isOverSizeLimit: boolean
) => {
  if (!hasExtracted)
    return "Action disabled since none of selected samples contains extracted files.";
  if (isOverSizeLimit)
    return `Total extracted file count over limit (${bulkDownloadMaxFiles}).`;
  return null;
};

const getDeleteMessage = (isEveryExtracted: boolean) => {
  if (isEveryExtracted) return "Extracted files cannot be deleted.";
  return "You can only delete your own samples.";
};

const canFetchFn = (sample: EnrichedFile) => {
  if (!sample.downloadable) return false;
  return sample.file_size <= ticloudMaxFetchFileSize;
};

const getCanFetch = (samples: Array<EnrichedFile>) => samples.some(canFetchFn);

const LocalBulkActions = ({ id, onToggle }: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null
  );

  const { selected } = useSelectRowsContext();
  const { mutateAsync: fetchAndAnalyze } = useFetchAnalyze();

  if (isEmpty(selected)) return null;

  const samples = Object.values(selected) as Array<EnrichedFile>;
  const hashes = samples.map(({ sha1 }) => sha1);

  const localSamples = samples.filter(s => s.location === LOCATION.LOCAL);
  const cloudSamples = samples.filter(s => s.location === LOCATION.CLOUD);
  const localHashes = localSamples.map(({ sha1 }) => sha1);
  const cloudHashes = cloudSamples.map(({ sha1 }) => sha1);

  const hasExtractedFiles = getHasExtractedFiles(localSamples);
  const extractedFileCount = getExtractedFileCount(localSamples);
  const isFileCountOverLimit = extractedFileCount > bulkDownloadMaxFiles;
  const isEveryExtracted = getIsEveryExtracted(localSamples);
  const canFetch = getCanFetch(cloudSamples);

  const triggerAction = (
    label: string,
    applicable: EnrichedFile[],
    onConfirm: () => void
  ) => {
    const nonApplicable = samples.filter(s => !applicable.includes(s));
    if (nonApplicable.length > 0) {
      setConfirmation({ label, applicable, nonApplicable, onConfirm });
    } else {
      onConfirm();
    }
  };

  const actions = [
    {
      label: BULK_ACTION_LABELS.Reanalyze,
      show: localSamples.length > 0,
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.Reanalyze, localSamples, () =>
          onToggle(
            { samples: localHashes.map(hash => ({ hash_value: hash })) },
            ActionsModalNames.Reanalyze
          )
        )
    },
    {
      label: BULK_ACTION_LABELS.DownloadSamples,
      show: localSamples.length > 0,
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.DownloadSamples, localSamples, () => {
          const downloads = localSamples.map(({ sha1, filename }) => ({
            hash: sha1,
            filename: filename || sha1
          }));
          onToggle({ samples: downloads }, ActionsModalNames.DownloadBulk);
        })
    },
    {
      label: BULK_ACTION_LABELS.DownloadExtractedFiles,
      show: localSamples.length > 0,
      disabled: !hasExtractedFiles || isFileCountOverLimit,
      disabledMessage: getExtractedFilesMessage(
        hasExtractedFiles,
        isFileCountOverLimit
      ),
      onClick: () =>
        triggerAction(
          BULK_ACTION_LABELS.DownloadExtractedFiles,
          localSamples,
          () => {
            const sampleDownloadData = localSamples.map(
              ({ sha1, filename }) => ({
                hash: sha1,
                filename: filename || sha1
              })
            );
            onToggle(
              { samples: sampleDownloadData },
              ActionsModalNames.DownloadBulkUnpacked
            );
          }
        )
    },
    {
      label: BULK_ACTION_LABELS.ApplyUserTags,
      show: localSamples.length > 0,
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.ApplyUserTags, localSamples, () =>
          onToggle(
            { hashes: localHashes.slice(0, GRID_SELECTION_TAG_LIMIT) },
            ActionsModalNames.ApplyTags
          )
        )
    },
    {
      label: BULK_ACTION_LABELS.SetClassification,
      show: localSamples.length > 0,
      disabled: localHashes.length > GRID_SELECTION_TAG_LIMIT,
      disabledMessage:
        GRID_SCHEMA_MESSAGES.DISABLED_WHEN_SELECTION_LIMIT_EXCEEDED(
          GRID_SELECTION_TAG_LIMIT
        ),
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.SetClassification, localSamples, () =>
          onToggle(
            { hashes: localHashes },
            ActionsModalNames.BulkUserClassification
          )
        )
    },
    {
      label: BULK_ACTION_LABELS.Delete,
      show: localSamples.length > 0,
      disabled: isEveryExtracted || !canUserDelete(localSamples),
      disabledMessage: getDeleteMessage(isEveryExtracted),
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.Delete, localSamples, () =>
          onToggle({ hashes: localHashes }, ActionsModalNames.Delete)
        )
    },
    {
      label: BULK_ACTION_LABELS.FetchAndAnalyze,
      show: cloudSamples.length > 0,
      disabled: !canFetch,
      disabledMessage: `${"Sample".pluralize(
        cloudHashes.length
      )} are unavailable for fetch`,
      onClick: () =>
        triggerAction(BULK_ACTION_LABELS.FetchAndAnalyze, cloudSamples, () =>
          fetchAndAnalyze({ sha1_hashes: cloudHashes })
        )
    },
    {
      label: BULK_ACTION_LABELS.FetchAndAnalyzeAdvanced,
      show: cloudSamples.length > 0,
      disabled: !canFetch,
      disabledMessage: `${"Sample".pluralize(
        cloudHashes.length
      )} are unavailable for fetch`,
      onClick: () =>
        triggerAction(
          BULK_ACTION_LABELS.FetchAndAnalyzeAdvanced,
          cloudSamples,
          () => setShowAdvanced(true)
        )
    },
    {
      label: `${BULK_ACTION_LABELS.Subscribe} ${
        samples.length > maxNumberSubscriptionHashes
          ? `(up to ${maxNumberSubscriptionHashes})`
          : ""
      }`,
      show: FEATURE_ALERTFEED,
      onClick: () => {
        const subscriptionData = samples.map(sample => ({
          location: sample.location,
          sha1: sample.sha1,
          available: sample.downloadable
        }));
        onToggle(
          {
            subscriptionHashes: subscriptionData,
            action: SUBSCRIPTION_ACTIONS.SUBSCRIBE
          },
          ActionsModalNames.Subscribehashes
        );
      }
    },
    {
      label: BULK_ACTION_LABELS.Unsubscribe,
      show: FEATURE_ALERTFEED,
      onClick: () => {
        onToggle(
          {
            subscriptionHashes: hashes,
            action: SUBSCRIPTION_ACTIONS.UNSUBSCRIBE
          },
          ActionsModalNames.Unsubscribehashes
        );
      }
    }
  ];

  return (
    <>
      <ActionsMenu
        id={id}
        actions={actions.filter(({ show }) => show)}
        stopPropagationOnClick={false}
        buttonComponent={Button}
      />
      <RenderWhen condition={Boolean(showAdvanced)}>
        <AdvancedFetchModal
          hashes={cloudHashes}
          onClose={() => setShowAdvanced(false)}
        />
      </RenderWhen>
      <RenderWhen condition={Boolean(confirmation)}>
        <BulkActionConfirmModal
          actionLabel={confirmation?.label ?? ""}
          applicableSamples={confirmation?.applicable ?? []}
          nonApplicableSamples={confirmation?.nonApplicable ?? []}
          onConfirm={() => {
            confirmation?.onConfirm();
            setConfirmation(null);
          }}
          onClose={() => setConfirmation(null)}
        />
      </RenderWhen>
    </>
  );
};

export default LocalBulkActions;
